import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * <ClubYieldCard clubId />
 * Compact yield widget shown inside a club page. Only the club creator can
 * toggle. Members see the pot's projected annual earnings for transparency.
 */
export default function ClubYieldCard({ clubId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/yield/club/${clubId}`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clubId) return;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [clubId]);

  const toggle = async (next) => {
    if (!data?.is_creator) return;
    setToggling(true);
    try {
      const path = next ? 'opt-in' : 'opt-out';
      await axios.post(
        `${BACKEND_URL}/api/yield/${path}`,
        { target_type: 'club', club_id: clubId, consent_version: 'yield-sim-v1' },
        { withCredentials: true }
      );
      toast.success(next ? 'Club yield enabled' : 'Club yield paused');
      await load();
    } catch {
      toast.error('Could not update club yield');
    } finally {
      setToggling(false);
    }
  };

  if (loading) return null;
  if (!data) return null;

  const apy = (data.apy_pct || 0).toFixed(2);
  const earnings = (data.accrued_earnings || 0).toFixed(4);
  const projected = (data.projected_annual || 0).toFixed(2);
  const principal = (data.current_principal || 0).toFixed(2);

  return (
    <Card
      data-testid="club-yield-card"
      className="relative overflow-hidden border-emerald-500/30 bg-gradient-to-br from-emerald-950/50 via-neutral-900 to-neutral-950"
    >
      <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/15 blur-3xl" />
      <CardHeader className="relative pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-white text-base">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              Club Pot Yield
              <Badge className="ml-1 bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[10px]">
                {apy}% APY
              </Badge>
              <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">
                SIMULATED
              </Badge>
            </CardTitle>
            <CardDescription className="text-neutral-400 mt-1 text-xs">
              Earn yield on the pot between rotations. Split evenly among members.
            </CardDescription>
          </div>
          {data.is_creator && (
            <Switch
              data-testid="club-yield-toggle"
              checked={!!data.enabled}
              disabled={toggling}
              onCheckedChange={toggle}
            />
          )}
        </div>
      </CardHeader>

      <CardContent className="relative space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded border border-neutral-800 bg-neutral-950/60 px-2 py-2">
            <div className="text-[9px] uppercase tracking-wider text-neutral-500">Pot</div>
            <div className="text-sm font-bold text-white">${principal}</div>
          </div>
          <div className="rounded border border-neutral-800 bg-neutral-950/60 px-2 py-2">
            <div className="text-[9px] uppercase tracking-wider text-neutral-500">Earned</div>
            <div className="text-sm font-bold text-emerald-300">${earnings}</div>
          </div>
          <div className="rounded border border-neutral-800 bg-neutral-950/60 px-2 py-2">
            <div className="text-[9px] uppercase tracking-wider text-neutral-500">Projected /yr</div>
            <div className="text-sm font-bold text-emerald-300">${projected}</div>
          </div>
        </div>
        {!data.is_creator && !data.enabled && (
          <p className="text-[11px] text-neutral-500">Ask your club creator to enable yield on the pot.</p>
        )}
        <div className="flex items-start gap-1.5 text-[10px] text-neutral-500">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{data.disclosure}</span>
        </div>
      </CardContent>
    </Card>
  );
}
